import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { quranApi } from '@/services/quranApiService'
import { useTranslation } from 'react-i18next'
import { mergeQuranTranslationLanguageCodes, quranTranslationLanguageLabel } from '@/utils/quranTranslationLanguages'

export default function ReaderPage() {
  const params = useParams()
  const surahNumber = Number(params.surahNumber)
  const { t } = useTranslation()
  const [lang, setLang] = useState<string>('en')
  const [languages, setLanguages] = useState<string[]>([])
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [reciters, setReciters] = useState<{ id: string; name: string; nameEn: string }[]>([])
  const [reciterId, setReciterId] = useState<string | undefined>(undefined)
  const [tracks, setTracks] = useState<{ id: string; url: string; quality: number; reciterId: string }[]>([])
  const bestTrack = useMemo(() => tracks[0], [tracks])
  const [tafsirLang, setTafsirLang] = useState<string | undefined>(undefined)
  const [tafsir, setTafsir] = useState<{ [num: number]: string | null }>({})

  useEffect(() => {
    quranApi
      .getTranslationLanguages()
      .then((langs) => setLanguages(mergeQuranTranslationLanguageCodes(langs)))
      .catch(() => setLanguages(mergeQuranTranslationLanguageCodes(['en'])))
    quranApi.getReciters().then((r) => {
      setReciters(r)
      if (r.length > 0) setReciterId(r[0].id)
    })
  }, [])

  useEffect(() => {
    setLoading(true)
    quranApi.getReader(surahNumber, lang).then(setData).finally(() => setLoading(false))
  }, [surahNumber, lang])

  useEffect(() => {
    quranApi.getSurahAudio(surahNumber, reciterId).then(setTracks).catch(() => setTracks([]))
  }, [surahNumber, reciterId])

  useEffect(() => {
    if (!tafsirLang) { setTafsir({}); return }
    quranApi.getTafsir(surahNumber, tafsirLang).then((res) => {
      const map: { [k: number]: string | null } = {}
      for (const a of res.ayahs) { map[a.number] = a.text }
      setTafsir(map)
    }).catch(() => setTafsir({}))
  }, [surahNumber, tafsirLang])

  if (loading) return <div className="p-4">{t('common.loading')}</div>
  if (!data) return <div className="p-4">{t('public.quran.notFound')}</div>

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">{data.surah.name} ({data.surah.number})</h1>
        <select className="border rounded px-2 py-1" value={lang} onChange={(e) => setLang(e.target.value)}>
          {languages.map((l) => (
            <option key={l} value={l}>
              {quranTranslationLanguageLabel(l)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm">{t('public.quran.reader')}</label>
        <select className="border rounded px-2 py-1" value={reciterId} onChange={(e) => setReciterId(e.target.value)}>
          {reciters.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        {bestTrack && (
          <audio controls src={bestTrack.url} className="w-full" />
        )}
      </div>
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm">القارئ:</label>
        <select className="border rounded px-2 py-1" value={reciterId} onChange={(e) => setReciterId(e.target.value)}>
          {reciters.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
        {bestTrack && (
          <audio controls src={bestTrack.url} className="w-full" />
        )}
      </div>
      <div className="flex items-center gap-3 mb-6">
        <label className="text-sm">{t('public.quran.tafsir')}</label>
        <select className="border rounded px-2 py-1" value={tafsirLang || ''} onChange={(e) => setTafsirLang(e.target.value || undefined)}>
          <option value="">{t('public.quran.none')}</option>
          {languages.map((l) => (
            <option key={l} value={l}>
              {quranTranslationLanguageLabel(l)}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-4">
        {data.ayahs.map((a: any) => (
          <div key={a.number} className="p-4 rounded border">
            <div className="text-xl text-right font-arabic leading-loose">{a.arabic}</div>
            {a.translation && <div className="text-sm text-gray-700 mt-2">{a.translation}</div>}
            {tafsirLang && tafsir[a.number] && (
              <div className="text-sm text-gray-700 mt-2 italic">{tafsir[a.number]}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}


